import { OnboardingFormData } from '@/app/api/user/onboarded/route';
import { BillingTransaction, Chat, ChatMessage, CreditsTransaction, ExamSession, ExamTicket, InventoryItem, Message, OralCorrection, PracticeResponseRecommendation, PracticeSession, PracticeVarsOption, SupportTicket, TodoItem, TodoList, Tutor, User, UserContactInfo, UserLangProgress, UserVocab, Vocab } from '@/components/Types';
import { getCurrentBearerToken } from '@/lib/client-auth';
import { isBlank } from '@/lib/utils';
import { setCurrentPractice } from '@/store/reducers/appSlice';
import { LANGUAGES, LEARNABLE_LANGUAGES, NATIVE_LANGUAGES } from './constants';
import { RecommendResponseRequest, RecommendResponseResponse } from '@/app/api/practice-sessions/[id]/recommend-response/route';
import { PracticeSessionTranslateRequestBody } from '@/app/api/practice-sessions/[id]/translate/route';
import { ClaimCouponResponseBody } from '@/app/api/user/claim-coupon/route';
import { CreateSupportTicketRequestBody } from '@/app/api/support/tickets/route';
import { UserLangProgressEventPostRequestBody, UserLangProgressEventResponse } from '@/app/api/user/lang-progress/event/route';
import { TestVoiceResponse } from '@/app/api/chat/test-voice/route';
import { ExamSessionUrlResponse } from '@/app/api/exam-sessions/[id]/url/route';
import { LocaleGetFromOtherRequestBody } from '@/app/api/locales/from_other/route';
import { LogEventCreateRequestBody } from '@/app/api/ev/[id]/route';
import { PracticeSessionCustomizeRequestBody } from '@/app/api/practice-sessions/customize/route';
import { ExamAssessmentResponse } from '@/app/api/exam/[id]/assess/route';
import { CreateChatRequestBody } from '@/app/api/chat/route';
import { ProcessCorrectionsResponse } from '@/app/api/chat/[id]/process-corrections/route';
import { CmsAgentGoal } from '@/components/Types';
import { LearningArticle } from '@/lib/types/articles';
import { LearningFeedPost, LearningFeedPostViewTelemetry } from './types/feed';
import { LearningFeedPostResponse } from '@/app/api/feed/learning/fetch/route';
import { LearningFeedTelemetryResponse } from '@/app/api/feed/learning/telemetry/route';
import { LearningFeedReactResponse, ReactRequestBody } from '@/app/api/feed/post/[id]/react/route';
import { UpdateQuizRequestBody, UpdateQuizResponse } from '@/app/api/feed/post/[id]/quiz/route';
import { RecommendChatResponseRequestBody } from '@/app/api/chat/[id]/messages/[messageId]/recommend-response/route';

async function loadCurrentUser() : Promise<User | null> {
    try {
        const response = await fetch(`/api/user`, {
            headers: {
                'Authorization': getCurrentBearerToken()
            }
        });

        const data = await response.json();
        const user = data.user;

        return user as User | null;
    } catch (error) {
        console.error('Error loading current user:', error);
        throw error;
    }
}

async function createExamSession(payload: { userId: string, motherLanguage: string, targetLanguage: string, userLevel: string, examStage: string, examType: string, examTicket?: ExamTicket | null, previousSessionId?: string | null, examId?: string | null }) {
    if (isBlank!(payload.userId)) {
        throw new Error('User ID is required');
    }

    try {
        const response = await fetch('/api/exam-sessions', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': getCurrentBearerToken()
            },
            body: JSON.stringify(payload)
        });

        const data = await response.json();
        const examSession = data.examSession;

        return examSession;
    } catch (error) {
        console.error('Error creating exam session:', error);
        throw error;
    }
}

async function requestExamAssessment(examId: string) : Promise<ExamAssessmentResponse> {
    try {
        const response = await fetch(`/api/exam/${examId}/assess`, {
            method: 'POST',
            headers: {
                'Authorization': getCurrentBearerToken()
            },
            body: JSON.stringify({ forceOverwrite: false })
        });

        const data = await response.json() as ExamAssessmentResponse;

        return data;
    } catch (error: any) {
        console.error('Error requesting exam assessment:', error);
        return { success: false, error: error?.message ?? error.toString(), examId: examId };
    }
}

async function loadExamSession(id: string) : Promise<ExamSession | null> {
    try {
        const response = await fetch(`/api/exam-sessions/${id}`, {
            headers: {
                'Authorization': getCurrentBearerToken()
            }
        });

        const data = await response.json();
        const examSession = data.examSession;

        return examSession as ExamSession | null;
    } catch (error) {
        console.error('Error loading exam session:', error);
        throw error;
    }
}

async function loadSessionsForExam(examId: string) : Promise<ExamSession[]> {
    try {
        const response = await fetch(`/api/exam/${examId}/sessions`, {
            headers: {
                'Authorization': getCurrentBearerToken()
            }
        });

        const data = await response.json();
        const examSessions = data.examSessions;

        return examSessions as ExamSession[];
    } catch (error) {
        console.error('Error loading sessions for exam:', error);
        throw error;
    }
}

async function updateExamSessionConversationId(id: string, conversationId: string) : Promise<void> {
}

async function completeExamSession(id: string, messages: Message[], vocab: Vocab[], corrections: OralCorrection[]) : Promise<ExamSession | null> {
    try {
        const response = await fetch(`/api/exam-sessions/${id}/complete`, {
            method: 'POST',
            headers: {
                'Authorization': getCurrentBearerToken()
            },
            body: JSON.stringify({ messages, vocab, corrections })
        });

        const data = await response.json();
        const examSession = data.examSession;

        return examSession as ExamSession | null;
    } catch (error) {
        console.error('Error completing exam session:', error);
        throw error;
    }
}

async function sendExamSummary(id: string) : Promise<{success: boolean}> {
    try {
        const response = await fetch(`/api/exam/${id}/send-summary`, {
            method: 'POST',
            headers: {
                'Authorization': getCurrentBearerToken()
            }
        });

        const data = await response.json();

        return {
            success: data.success,
        };
    } catch (error) {
        console.error('Error sending exam summary:', error);
        throw error;
    }
}

async function createPracticeSession(payload: { userId: string, motherLanguage: string, targetLanguage: string, userLevel: string }) {
    if (isBlank!(payload.userId)) {
        throw new Error('User ID is required');
    }

    try {
        const response = await fetch('/api/practice-sessions', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': getCurrentBearerToken()
            },
            body: JSON.stringify(payload)
        });

        const data = await response.json();

        const practiceSession = data.practiceSession;

        return practiceSession;
    } catch (error) {
        console.error('Error creating practice session:', error);
        throw error;
    }
}

async function loadPracticeSessions(payload: { language: string, limit: number, offset: number }) : Promise<Partial<PracticeSession>[]> {
    try {
        const response = await fetch(`/api/practice-sessions?language=${payload.language}&limit=${payload.limit}&offset=${payload.offset}`, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': getCurrentBearerToken()
            }
        });

        const data = await response.json();
        const practiceSessions = data.practiceSessions;

        return practiceSessions as Partial<PracticeSession>[];
    } catch (error) {
        console.error('Error loading practice sessions:', error);
        throw error;
    }
}

async function loadPracticeSession(id: string) : Promise<PracticeSession | null> {
    try {
        const response = await fetch(`/api/practice-sessions/${id}`, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': getCurrentBearerToken()
            }
        });

        const data = await response.json();
        const practiceSession = data.practiceSession;

        return practiceSession as PracticeSession | null;
    } catch (error) {
        console.error('Error loading practice session:', error);
        throw error;
    }
}

async function getPracticeSessionResponseRecommendations(id: string, messages: Message[], motherLanguage: keyof typeof NATIVE_LANGUAGES, userGoal?: string | null) : Promise<PracticeResponseRecommendation[]> {
    const LOGTAG = "getPracticeSessionResponseRecommendations";

    try {
        const payload = {
            messages,
            motherLanguage,
            userGoal
        } as RecommendResponseRequest;

        const response = await fetch(`/api/practice-sessions/${id}/recommend-response`, {
            method: 'POST',
            headers: {
                'Authorization': getCurrentBearerToken()
            },
            body: JSON.stringify(payload)
        });

        const data = await response.json() as RecommendResponseResponse;

        if (data?.recommendations) {
            return data.recommendations;
        } else {
            console.error(LOGTAG, 'Error:', "Wrong response structure");
            return [];
        }
    } catch (error) {
        console.error(LOGTAG, 'Error:', error);
        throw error;
    }
}

async function getExamSessionResponseRecommendations(id: string, messages: Message[], motherLanguage: keyof typeof NATIVE_LANGUAGES) : Promise<PracticeResponseRecommendation[]> {
    const LOGTAG = "getExamSessionResponseRecommendations";

    // TODO: Implement

    return [] as PracticeResponseRecommendation[];
}

async function getChatResponseRecommendations(id: string, messages: ChatMessage[], motherLanguage: keyof typeof NATIVE_LANGUAGES, userGoal?: string | null) : Promise<PracticeResponseRecommendation[]> {
    const LOGTAG = "getChatResponseRecommendations";

    try {
        const payload = {
            messages,
            motherLanguage,
            userGoal
        } as RecommendChatResponseRequestBody;

        const response = await fetch(`/api/chat/${id}/messages/${messages[messages.length - 1]._id}/recommend-response`, {
            method: 'POST',
            headers: {
                'Authorization': getCurrentBearerToken()
            },
            body: JSON.stringify(payload)
        });

        const data = await response.json() as RecommendResponseResponse;

        if (data?.recommendations) {
            return data.recommendations;
        } else {
            console.error(LOGTAG, 'Error:', "Wrong response structure");
            return [];
        }
    } catch (error) {
        console.error(LOGTAG, 'Error:', error);
        throw error;
    }
}

async function updatePracticeSessionConversationId(id: string, conversationId: string) : Promise<void> {
}

async function completePracticeSession(id: string, messages: Message[], vocab: Vocab[], corrections: OralCorrection[]) : Promise<PracticeSession | null> {
    try {
        const response = await fetch(`/api/practice-sessions/${id}/complete`, {
            method: 'POST',
            headers: {
                'Authorization': getCurrentBearerToken()
            },
            body: JSON.stringify({ messages, vocab, corrections })
        });

        const data = await response.json();
        const practiceSession = data.practiceSession;

        return practiceSession as PracticeSession | null;
    } catch (error) {
        console.error('Error completing practice session:', error);
        throw error;
    }
}

async function sendPracticeSessionSummary(id: string) : Promise<PracticeSession | null> {
    try {
        const response = await fetch(`/api/practice-sessions/${id}/send-summary`, {
            method: 'POST',
            headers: {
                'Authorization': getCurrentBearerToken()
            }
        });

        const data = await response.json();
        const practiceSession = data.practiceSession;

        return practiceSession as PracticeSession | null;
    } catch (error) {
        console.error('Error completing practice session:', error);
        throw error;
    }
}

async function requestPracticeSessionAssessment(id: string) : Promise<PracticeSession | null> {
    try {
        const response = await fetch(`/api/practice-sessions/${id}/assess`, {
            method: 'POST',
            headers: {
                'Authorization': getCurrentBearerToken()
            }
        });

        const data = await response.json();
        const practiceSession = data.practiceSession;

        return practiceSession as PracticeSession | null;
    } catch (error) {
        console.error('Error completing practice session:', error);
        throw error;
    }
}

async function loadUserLangProgress(language: string) : Promise<UserLangProgress | null> {
    if (isBlank!(language)) {
        throw new Error('Language is required');
    }

    try {
        const response = await fetch(`/api/user/lang-progress?language=${language.toLowerCase()}`, {
            headers: {
                'Authorization': getCurrentBearerToken()
            }
        });

        const data = await response.json();
        const userLangProgress = data.userLangProgress;

        return userLangProgress as UserLangProgress | null;
    } catch (error) {
        console.error('Error loading user language progress:', error);
        throw error;
    }
}

async function loadUserLangProgressVocab(language: string) : Promise<{ current: Vocab[], learned: Vocab[], dismissed: string[] } | null> {
    if (isBlank!(language)) {
        throw new Error('Language is required');
    }

    try {
        const response = await fetch(`/api/user/lang-progress/vocab?language=${language.toLowerCase()}`, {
            headers: {
                'Authorization': getCurrentBearerToken()
            }
        });

        const data = await response.json();

        if (data) {
            return {
                current: data.current,
                learned: data.learned,
                dismissed: data.dismissed,
            };
        }

        return null;
    } catch (error) {
        console.error('Error loading user language progress:', error);
        throw error;
    }
}

async function loadUserLangProgressCorrections(language: string) : Promise<{ current: OralCorrection[], dismissed: OralCorrection[] } | null> {
    if (isBlank!(language)) {
        throw new Error('Language is required');
    }

    try {
        const response = await fetch(`/api/user/lang-progress/corrections?language=${language.toLowerCase()}`, {
            headers: {
                'Authorization': getCurrentBearerToken()
            }
        });

        const data = await response.json();
        const userLangProgress = data.userLangProgress as UserLangProgress | null;

        if (userLangProgress) {
            return {
                current: userLangProgress.corrections,
                dismissed: userLangProgress.dismissedCorrections,
            };
        }

        return null;
    } catch (error) {
        console.error('Error loading user language progress:', error);
        throw error;
    }
}

async function getUserRewards(language: string) : Promise<Partial<InventoryItem>[] | null> {
    if (isBlank!(language)) {
        throw new Error('Language is required');
    }

    try {
        const response = await fetch(`/api/user/rewards?language=${language.toLowerCase()}`, {
            headers: {
                'Authorization': getCurrentBearerToken(),
            }
        });

        const data = await response.json();
        const rewards = data.rewards;

        return rewards as Partial<InventoryItem>[] | null;
    } catch (error) {
        console.error('Error loading user rewards:', error);
        throw error;
    }
}           

async function initSession() : Promise<void> {
    try {
        const response = await fetch('/api/user/init-session', {
            headers: {
                'Authorization': getCurrentBearerToken()
            }
        });

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error initializing session:', error);
        throw error;
    }
}

// Deprecated
async function markVocabLearned(language: string, word: string) : Promise<void> {
    if (isBlank!(language)) {
        throw new Error('Language is required');
    }

    if (isBlank!(word)) {
        throw new Error('Word is required');
    }

    try {
        const response = await fetch(`/api/user/lang-progress/vocab?language=${language.toLowerCase()}`, {
            method: 'PATCH',
            headers: {
                'Authorization': getCurrentBearerToken()
            },
            body: JSON.stringify({ learned: [word], dismissed: [] })
        });

        const data = await response.json();
    } catch (error) {
        console.error('Error loading user language progress:', error);
        throw error;
    }
}

// Deprecated
async function markVocabDismissed(language: string, word: string) : Promise<void> {
    if (isBlank!(language)) {
        throw new Error('Language is required');
    }

    if (isBlank!(word)) {
        throw new Error('Word is required');
    }

    try {
        const response = await fetch(`/api/user/lang-progress/vocab?language=${language.toLowerCase()}`, {
            method: 'PATCH',
            headers: {
                'Authorization': getCurrentBearerToken()
            },
            body: JSON.stringify({ dismissed: [word], learned: [] })
        });

        const data = await response.json();
    } catch (error) {
        console.error('Error updating user language progress:', error);
        throw error;
    }
}

async function markCorrectionDismissed(language: string, correction: OralCorrection) : Promise<void> {
    if (isBlank!(language)) {
        throw new Error('Language is required');
    }

    if (correction == null || isBlank(correction.parentPhrase)) {
        throw new Error('Correction is missing or empty: ' + JSON.stringify(correction));
    }

    try {
        const response = await fetch(`/api/user/lang-progress/corrections?language=${language.toLowerCase()}`, {
            method: 'PATCH',
            headers: {
                'Authorization': getCurrentBearerToken()
            },
            body: JSON.stringify({ dismissed: [correction.parentPhrase] })
        });

        const data = await response.json();
    } catch (error) {
        console.error('Error updating user language progress:', error);
        throw error;
    }
}

export interface EditableUserPreferences {
    firstName?: string;
    lastName?: string;
    motherLanguage?: string;
    userLevel?: string;
    learningLanguage?: string;
    notifications?: boolean;
    theme?: string;
    seenPracticeTutorial?: boolean;
    telegramPracticeReminders?: boolean;
    telegramPracticeRemindersPeriod?: 'frequent' | 'moderate' | 'rare' | null;
};

async function updateUserPreferences(payload: EditableUserPreferences): Promise<{ user: User, userLangProgress: UserLangProgress }> {
    try {
        const response = await fetch('/api/user/preferences', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': getCurrentBearerToken()
            },
            body: JSON.stringify(payload)
        });

        const data = await response.json();
        const user = data.user as User;
        const userLangProgress = data.userLangProgress as UserLangProgress;

        const result = {
            user,
            userLangProgress
        }

        return result;
    } catch (error) {
        console.error('Error updating user preferences:', error);
        throw error;
    }
}

async function loadUserLangLevels() : Promise<Record<string, string>> {
    try {
        const response = await fetch('/api/user/lang-levels', {
            headers: {
                'Authorization': getCurrentBearerToken()
            }
        });

        const data = await response.json();
        const languageLevels = data.languageLevels;

        return languageLevels;
    } catch (error) {
        console.error('Error loading user language levels:', error);
        throw error;
    }
}

async function loadPracticeSessionCustomization(payload: PracticeSessionCustomizeRequestBody) : Promise<PracticeVarsOption | null> {
    try {
        const response = await fetch(`/api/practice-sessions/customize`, {
            method: 'POST',
            headers: {
                'Authorization': getCurrentBearerToken()
            },
            body: JSON.stringify(payload)
        });

        const data = await response.json();
        const customization = data.customization;

        return customization as PracticeVarsOption | null;
    } catch (error) {
        console.error('Error loading practice session customization:', error);
        throw error;
    }
}

async function loadTutors(language: string, level: string, examStage?: string | null) : Promise<Tutor[]> {
    try {
        const response = await fetch(`/api/tutors?language=${language}&level=${level}&examStage=${examStage || ''}`, {
            headers: {
                'Authorization': getCurrentBearerToken()
            }
        });

        const data = await response.json();
        const tutors = data.tutors;

        return tutors as Tutor[];
    } catch (error) {
        console.error('Error loading tutors:', error);
        throw error;
    }
}

async function loadTutor(id: string) : Promise<Tutor | null> {
    try {
        const response = await fetch(`/api/contacts/tutor/${id}`, {
            headers: {
                'Authorization': getCurrentBearerToken()
            }
        });

        const data = await response.json();
        const tutor = data.tutor;

        return tutor as Tutor | null;
    } catch (error) {
        console.error('Error loading tutor:', error);
        throw error;
    }
}

async function submitOnboardingForm(payload: Partial<OnboardingFormData>) : Promise<{success: boolean, alreadyOnboarded?: boolean}> {
    try {
        const response = await fetch(`/api/user/onboarded`, {
            method: 'PATCH',
            headers: {
                'Authorization': getCurrentBearerToken()
            },
            body: JSON.stringify(payload)
        });

        const data = await response.json();

        return data;
    } catch (error) {
        console.error('Error loading user language progress:', error);
        throw error;
    }
}

async function notifyPracticeConversationStarted(practiceSessionId: string, conversationId: string) : Promise<{success: boolean} | null> {
    try {
        const response = await fetch(`/api/practice-sessions/${practiceSessionId}/conversation-started`, {
            method: 'POST',
            headers: {
                'Authorization': getCurrentBearerToken()
            },
            body: JSON.stringify({ conversationId })
        });

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error notifying practice conversation started:', error);
        throw error;
    }
}

async function notifyExamConversationStarted(examSessionId: string, conversationId: string) : Promise<{success: boolean} | null> {
    try {
        const response = await fetch(`/api/exam-sessions/${examSessionId}/conversation-started`, {
            method: 'POST',
            headers: {
                'Authorization': getCurrentBearerToken()
            },
            body: JSON.stringify({ conversationId })
        });

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error notifying practice conversation started:', error);
        throw error;
    }
}

async function getTranslationForPracticeSession(practiceSessionId: string, message: Message, fromLanguage: string, toLanguage: string) : Promise<string> {
    try {
        const payload = {
            message,
            motherLanguage: fromLanguage,
            targetLanguage: toLanguage
        } as PracticeSessionTranslateRequestBody;

        const response = await fetch(`/api/practice-sessions/${practiceSessionId}/translate`, {
            method: 'POST',
            headers: {
                'Authorization': getCurrentBearerToken()
            },
            body: JSON.stringify(payload)
        });

        const data = await response.json();
        const translation = data.translation;

        return translation as string;
    } catch (error) {
        console.error('Error getting translation for practice session:', error);
        throw error;
    }
}

async function getTranslationForExamSession(examSessionId: string, message: Message, fromLanguage: string, toLanguage: string) : Promise<string> {
    try {
        const payload = {
            message,
            motherLanguage: fromLanguage,
            targetLanguage: toLanguage
        } as PracticeSessionTranslateRequestBody;

        const response = await fetch(`/api/exam-sessions/${examSessionId}/translate`, {
            method: 'POST',
            headers: {
                'Authorization': getCurrentBearerToken()
            },
            body: JSON.stringify(payload)
        });

        const data = await response.json();
        const translation = data.translation;

        return translation as string;
    } catch (error) {
        console.error('Error getting translation for exam session:', error);
        throw error;
    }
}

async function getTranslationForChat(chatId: string, message: Message, fromLanguage: string, toLanguage: string) : Promise<string> {
    try {
        const payload = {
            message,
            motherLanguage: fromLanguage,
            targetLanguage: toLanguage
        } as PracticeSessionTranslateRequestBody;

        const response = await fetch(`/api/chat/${chatId}/translate`, {
            method: 'POST',
            headers: {
                'Authorization': getCurrentBearerToken()
            },
            body: JSON.stringify(payload)
        });

        const data = await response.json();
        const translation = data.translation;

        return translation as string;
    } catch (error) {
        console.error('Error getting translation for exam session:', error);
        throw error;
    }
}

async function getContentBlock(language: string, key: string, fallbackLanguage: string | null, format: 'html' | null) : Promise<{ content: string, format: string }> {
    try {
        const response = await fetch(`/api/content-blocks?language=${language}&key=${key}&format=${format}&fallbackLanguage=${fallbackLanguage ?? ''}`, {});

        const data = await response.json();

        return {
            content: data.content,
            format: data.format
        };
    } catch (error) {
        console.error('Error getting content block:', error);
        return {
            content: '',
            format: 'text'
        }
    }
}

async function getLastBillingTransaction(type: 'credit-purchase' | 'credit-refund' | 'credit-adjustment') : Promise<{billingTransaction: BillingTransaction | null, creditTransaction: CreditsTransaction | null}> {
    try {
        const response = await fetch(`/api/user/last-billing-transaction?type=${type}`, {
            headers: {
                'Authorization': getCurrentBearerToken()
            }
        });

        const data = await response.json();
        const billingTransaction = data.lastTransaction;
        const creditTransaction = data.lastCreditTransaction;
        return {
            billingTransaction: billingTransaction as BillingTransaction | null,
            creditTransaction: creditTransaction as CreditsTransaction | null
        };
    } catch (error) {
        console.error('Error getting last billing transaction:', error);
        throw error;
    }
}

async function sendTelegramExplainer(payload: { vocab?: Vocab, correction?: OralCorrection, query?: string, motherLanguage?: string, learningLanguage?: string }) : Promise<{telegramLink: string}> {
    try {
        const response = await fetch(`/api/explain/in-telegram`, {
            method: 'POST',
            headers: {
                'Authorization': getCurrentBearerToken()
            },
            body: JSON.stringify(payload)
        });

        const data = await response.json();

        return {
            telegramLink: data.telegramLink
        };
    } catch (error) {
        console.error('Error explaining in telegram:', error);
        throw error;
    }
}

async function claimCoupon(couponCode: string) : Promise<ClaimCouponResponseBody> {
    try {
        const response = await fetch(`/api/user/claim-coupon`, {
            method: 'POST',
            headers: {
                'Authorization': getCurrentBearerToken()
            },
            body: JSON.stringify({ couponCode })
        });

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error claiming coupon:', error);
        throw error;
    }
}

async function getSupportTickets(userId: string) : Promise<SupportTicket[]> {
    try {
        const response = await fetch(`/api/support/tickets?userId=${userId}`, {
            headers: {
                'Authorization': getCurrentBearerToken()
            }
        });

        const data = await response.json();
        const tickets = data.tickets;

        return tickets as SupportTicket[];
    } catch (error) {
        console.error('Error getting support tickets:', error);
        throw error;
    }
}

async function createSupportTicket(payload: CreateSupportTicketRequestBody) : Promise<SupportTicket> {
    try {
        const response = await fetch(`/api/support/tickets`, {
            method: 'POST',
            headers: {
                'Authorization': getCurrentBearerToken()
            },
            body: JSON.stringify(payload)
        });

        const data = await response.json();
        const ticket = data.ticket;

        return ticket as SupportTicket;
    } catch (error) {
        console.error('Error creating support ticket:', error);
        throw error;
    }
}

async function createUserLangProgressEvent(payload: UserLangProgressEventPostRequestBody) : Promise<UserLangProgressEventResponse> {
    try {
        const response = await fetch(`/api/user/lang-progress/event`, {
            method: 'POST',
            headers: {
                'Authorization': getCurrentBearerToken()
            },
            body: JSON.stringify(payload)
        });

        const data = await response.json() as UserLangProgressEventResponse;
        return data;
    } catch (error) {
        console.error('Error creating user lang progress event:', error);
        throw error;
    }
}

async function getVocabListenUrl(text: string, language: string, userVocabId?: string) : Promise<{url: string | null}> {
    try {
        const response = await fetch(`/api/vocab/listen`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': getCurrentBearerToken()
            },
            body: JSON.stringify({ text, language, userVocabId })
        });

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error getting vocab listen url:', error);
        throw error;
    }
}

async function getAssistiveSpeechUrl(fragmentId: string, language: string) : Promise<{url: string | null, text: string | null}> {
    try {
        const response = await fetch(`/api/speech/assistive`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': getCurrentBearerToken()
            },
            body: JSON.stringify({ fragmentId, language })
        });

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error getting vocab listen url:', error);
        throw error;
    }
}

async function sendAudioMessage(language: string, audio: Blob, test: boolean = false) : Promise<void> {
    try {
        const formData = new FormData();
        formData.append('audio', audio, 'recording.webm');
        formData.append('language', language);
        formData.append('test', test ? 'true' : 'false');

        const response = await fetch(`/api/chat/receive-voice`, {
            method: 'POST',
            headers: {
                'Authorization': getCurrentBearerToken()
            },
            body: formData,
            credentials: 'include',
        });

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error sending audio message:', error);
        throw error;
    }
}

async function testVoice(expectedText: string, nativeLanguage: string, learningLanguage: string, audio: Blob) : Promise<TestVoiceResponse> {
    try {
        const formData = new FormData();
        formData.append('audio', audio, 'recording.webm');
        formData.append('nativeLanguage', nativeLanguage);
        formData.append('language', learningLanguage);
        formData.append('expectedText', expectedText);

        const response = await fetch(`/api/chat/test-voice`, {
            method: 'POST',
            headers: {
                'Authorization': getCurrentBearerToken()
            },
            body: formData,
            credentials: 'include',
        });

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error sending audio message:', error);
        throw error;
    }
}

async function getExamSessionUrl(examSessionId: string) : Promise<ExamSessionUrlResponse> {
    try {
        const response = await fetch(`/api/exam-sessions/${examSessionId}/url`, {
            method: 'POST',
            headers: {
                'Authorization': getCurrentBearerToken()
            }
        });

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error getting exam session url:', error);
        throw error;
    }
}

async function getTranslationsFromOtherLanguage(payload: LocaleGetFromOtherRequestBody) : Promise<{language: string, translations: string[]}> {
    try {
        const response = await fetch(`/api/locales/from_other`, {
            method: 'POST',
            headers: {
                'Authorization': getCurrentBearerToken()
            },
            body: JSON.stringify(payload)
        });

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error getting translations from other language:', error);
        throw error;
    }
}

export async function sendTgChatExercise(language: string) : Promise<{success: boolean, error?: string}> {
    try {
        const response = await fetch(`/api/exercises/send-tg`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': getCurrentBearerToken()
            }
        });

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error sending telegram chat exercise:', error);
        throw error;
    }
}

// Create a log event
export async function evCreate(payload: LogEventCreateRequestBody) : Promise<void> {
    try {
        if (payload.b) {
            // Encode name and sv (if present) to base64
            payload.name = Buffer.from(payload.name, 'utf-8').toString('base64');
            payload.sv = payload.sv ? Buffer.from(payload.sv, 'utf-8').toString('base64') : undefined;
        }

        const uid = Math.random().toString(36).substring(2, 15);

        await fetch(`/api/ev/${uid}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': getCurrentBearerToken()
            },
            body: JSON.stringify(payload)
        });
    } catch (error) {
        console.error('Error:', error);
        // throw error;
    }
}

export async function getChat(chatId: string) : Promise<Chat | null> {
    try {
        const response = await fetch(`/api/chat/${chatId}`, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': getCurrentBearerToken()
            }
        });

        const data = await response.json();
        return data.chat as Chat | null;
    } catch (error) {
        console.error('Error getting chat:', error);
        throw error;
    }
}

export async function getChats(chatType: string, chatRuleId?: string | null) : Promise<Chat[]> {
    try {
        const response = await fetch(`/api/chat`, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': getCurrentBearerToken()
            }
        });

        const data = await response.json();
        return data.chats as Chat[];
    } catch (error) {
        console.error('Error getting chats:', error);
        throw error;
    }
}

export async function getChatsForList({language, limit = 32, offset = 0}: {language: keyof typeof LANGUAGES, limit: number, offset: number}) : Promise<Chat[]> {
    try {
        const response = await fetch(`/api/chat?language=${language}&limit=${limit}&offset=${offset}`, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': getCurrentBearerToken()
            }
        });

        const data = await response.json();
        return data.chats as Chat[];
    } catch (error) {
        console.error('Error getting chats for list:', error);
        throw error;
    }
}

export async function createChat(payload: CreateChatRequestBody) : Promise<Chat> {
    try {
        const response = await fetch(`/api/chat`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': getCurrentBearerToken()
            },
            body: JSON.stringify(payload)
        });

        const data = await response.json();

        if (data.success) {
            return data.chat as Chat;
        } else {
            throw new Error(data.error ?? 'Unknown error');
        }
    } catch (error) {
        console.error('Error creating chat:', error);
        throw error;
    }
}

export async function getChatMessages({chatId, last = 64, limit = 64, offset = 0}: {chatId: string, last?: number, limit?: number, offset?: number}) : Promise<ChatMessage[]> {
    try {
        const response = await fetch(`/api/chat/${chatId}/messages?last=${last}&limit=${limit}&offset=${offset}`, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': getCurrentBearerToken()
            }
        });

        const data = await response.json();
        return data.messages as ChatMessage[];
    } catch (error) {
        console.error('Error getting chat messages:', error);
        throw error;
    }
}

export async function getChatMessage({chatId, messageId}: {chatId: string, messageId: string}) : Promise<ChatMessage | null> {
    try {
        const response = await fetch(`/api/chat/${chatId}/messages/${messageId}`, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': getCurrentBearerToken()
            }
        });

        const data = await response.json();
        return data.message as ChatMessage;
    } catch (error) {
        console.error('Error getting chat message:', error);
        throw error;
    }
}

async function sendChatMessage({chatId, language, audio, text, emotion, action}: {chatId: string, language: string, audio?: Blob, text?: string, emotion?: string, action?: string}) : Promise<{success: boolean, message?: ChatMessage, error?: string}> {
    try {
        const formData = new FormData();
        formData.append('chatId', chatId);
        formData.append('language', language);

        if (text) {
            formData.append('text', text);
        }
        if (audio) {
            // iOS Safari compatibility: always include filename and ensure proper MIME type
            const filename = audio.type.includes('wav') ? 'recording.wav' : 'recording.mp4';
            formData.append('audio', audio, filename);
        }

        if (emotion) {
            formData.append('emotion', emotion);
        }
        if (action) {
            formData.append('action', action);
        }

        // console.log('👉', 'Sending chat message', {
        //     hasAudio: !!audio,
        //     audioSize: audio?.size,
        //     audioType: audio?.type,
        //     textLength: text?.length
        // });

        const response = await fetch(`/api/chat/${chatId}/send`, {
            method: 'POST',
            headers: {
                'Authorization': getCurrentBearerToken(),
            },
            body: formData,
            credentials: 'include',
        });

        // Handle non-200 responses with more detailed error info
        if (!response.ok) {
            let errorData;
            try {
                errorData = await response.json();
            } catch (jsonError) {
                // Response body might not be JSON (e.g., nginx error page)
                errorData = {
                    error: 'network_error',
                    message: `HTTP ${response.status}: ${response.statusText}`,
                    responseText: await response.text().catch(() => 'Unable to read response')
                };
            }

            console.error('❌ Chat message send failed:', {
                status: response.status,
                statusText: response.statusText,
                errorData,
                hasAudio: !!audio,
                audioSize: audio?.size
            });

            return {
                success: false,
                error: errorData.error || 'request_failed'
            };
        }

        const data = await response.json();
        return data as {success: boolean, message?: ChatMessage, error?: string};
    } catch (error) {
        console.error('❌ Error sending chat message:', {
            error,
            hasAudio: !!audio,
            audioSize: audio?.size,
            audioType: audio?.type
        });

        return {
            success: false,
            error: 'network_error'
        };
    }
}

export async function getChatMessageAudio(chatId: string, messageId: string) : Promise<{blob: Blob}> {
    try {
        const response = await fetch(`/api/chat/${chatId}/messages/${messageId}/audio`, {
            headers: {
                'Authorization': getCurrentBearerToken()
            }
        });

        const data = await response.blob();

        if (!response.ok) {
            throw new Error('Failed to get chat message audio');
        }

        return { blob: data };
    } catch (error) {
        console.error('Error getting chat message audio:', error);
        throw error;
    }
}

export async function getWordExplanationPage(payload: { word: string, phrase: string, language: string, motherLanguage: string, wordsOnLeft?: string[], wordsOnRight?: string[], practiceSessionId?: string, examSessionId?: string, chatId?: string, messageId?: string, learningFeedPostId?: string }, signal?: AbortSignal | null) : Promise<{html: string, word: string}> {
    try {
        const response = await fetch(`/api/explain/word/as-page`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': getCurrentBearerToken()
            },
            body: JSON.stringify(payload),
            signal: signal ?? undefined
        });

        const data = await response.json();

        if (data.success) {
            return data;
        } else {
            throw new Error(data.error ?? 'Unknown error');
        }
    } catch (error) {
        console.error('Error getting word explanation page:', error);
        throw error;
    }
}

async function getTodoList(tag: string, language: keyof typeof LANGUAGES) : Promise<TodoList | null> {
    try {
        const response = await fetch(`/api/todo-lists/${tag}?language=${language}`, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': getCurrentBearerToken()
            }
        });

        const data = await response.json();
        return data.todoList as TodoList;
    } catch (error) {
        console.error('Error getting todo list:', error);
        throw error;
    }
}

async function setTodoItem({listId, itemId, completed, language, listTag}: {listId: string, itemId: string, completed: boolean, language?: string, listTag?: string}) : Promise<void> {
    try {
        if (!listId && listTag) {
            listId = listTag;
        }
        
        const response = await fetch(`/api/todo-lists/${listId}/item/${itemId}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': getCurrentBearerToken()
            },
            body: JSON.stringify({ completed, language, listTag })
        });
    } catch (error) {
        console.error('Error setting todo item:', error);
        throw error;
    }
}

async function getTodoItem({listTag, itemId, language, generateNotes = false, signal = undefined}: {listTag: string, itemId: string, language: keyof typeof LANGUAGES, generateNotes?: boolean, signal?: AbortSignal | null}) : Promise<TodoItem | null> {
    try {
        const response = await fetch(`/api/todo-lists/${listTag}/item/${itemId}?language=${language}&generateNotes=${generateNotes ? "true" : "false"}`, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': getCurrentBearerToken()
            },
            signal: signal ?? undefined
        });

        const data = await response.json();
        return data.todoItem as TodoItem;
    } catch (error) {
        console.error('Error getting todo item:', error);
        throw error;
    }
}

async function processChatCorrections({chatId, previousMessages, testedMessages}: {chatId: string, previousMessages: Partial<ChatMessage>[], testedMessages: Partial<ChatMessage>[]}) : Promise<ProcessCorrectionsResponse> {
    try {
        const response = await fetch(`/api/chat/${chatId}/process-corrections`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': getCurrentBearerToken()
            },
            body: JSON.stringify({ previousMessages, testedMessages })
        });

        const data = await response.json();
        return data as ProcessCorrectionsResponse;
    } catch (error) {
        console.error('Error processing chat corrections:', error);
        throw error;
    }
}

async function completeChat(chatId: string) : Promise<{success: boolean, chat?: Chat, error?: string}> {
    try {
        const response = await fetch(`/api/chat/${chatId}/complete`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': getCurrentBearerToken()
            }
        });

        const data = await response.json();
        return data as {success: boolean, chat?: Chat, error?: string};
    } catch (error) {
        console.error('Error completing chat:', error);
        return {
            success: error.data.success,
            error: error.data.error,
            chat: null
        }
    }
}

async function completeChatTasks(chatId: string, tasks: string[]) : Promise<{success: boolean, chat?: Chat, chatCompleted?: boolean, error?: string}> {
    try {
        const response = await fetch(`/api/chat/${chatId}/complete-tasks`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': getCurrentBearerToken()
            },
            body: JSON.stringify({ tasks })
        });

        const data = await response.json();
        return data as {success: boolean, chat?: Chat, error?: string};
    } catch (error: any) {
        console.error('Error completing chat tasks:', error);
        return {
            success: error.data.success,
            error: error.data.error,
        }
    }
}

export async function deleteAccount(confirmed: boolean) : Promise<{success: boolean, error?: string}> {
    try {
        const response = await fetch(`/api/user/delete-account`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': getCurrentBearerToken()
            },
            body: JSON.stringify({ confirmed })
        });

        const data = await response.json();
        return data as {success: boolean, error?: string};
    } catch (error) {
        console.error('Error deleting account:', error);
        throw error;
    }
}

export async function getChatGoals(language: string) : Promise<CmsAgentGoal[]> {
    try {
        const response = await fetch(`/api/chat-goals?language=${language}`, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': getCurrentBearerToken()
            }
        });

        const data = await response.json();
        return data.goals as CmsAgentGoal[];
    } catch (error) {
        console.error('Error getting chat goals:', error);
        throw error;
    }
}

async function getLearningArticleById(articleId: string) : Promise<LearningArticle | null> {
    try {
        const response = await fetch(`/api/learning-articles/${articleId}`, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': getCurrentBearerToken()
            }
        });

        const data = await response.json();
        return data.article as LearningArticle;
    } catch (error) {
        console.error('Error getting learning article:', error);
        throw error;
    }
}

async function getLearningArticlesForList({learnedLanguage, nativeLanguage, limit = 10, offset = 0}: {learnedLanguage: string, nativeLanguage: string, limit?: number, offset?: number}) : Promise<Partial<LearningArticle>[] | null> {
    try {
        const response = await fetch(`/api/learning-articles?t=t&learnedLanguage=${learnedLanguage}&nativeLanguage=${nativeLanguage}&limit=${limit}&offset=${offset}`, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': getCurrentBearerToken()
            }
        });

        const data = await response.json();
        return data.articles as Partial<LearningArticle>[];
    } catch (error) {
        console.error('Error getting learning articles for list:', error);
        throw error;
    }
}

interface LearningFeedPostFetchParams {
    language: string;
    nativeLanguage: string;
    limit: number;
    presentPostIds: string[];
    fetchMode?: 'realtime' | 'recent';
    gameLives?: number;
    lastGameId?: string;
    lastGamePostOffset?: number;
}

async function getLearningFeedPosts(params: LearningFeedPostFetchParams) : Promise<LearningFeedPostResponse | null> {
    try {
        const response = await fetch(`/api/feed/learning/fetch`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': getCurrentBearerToken()
            },
            body: JSON.stringify({ 
                present: params.presentPostIds, 
                limit: params.limit, 
                language: params.language, 
                nativeLanguage: params.nativeLanguage, 
                fetchMode: params.fetchMode,
                lastGameId: params.lastGameId,
                lastGamePostOffset: params.lastGamePostOffset,
                gameLives: params.gameLives,
            })
        });

        const data = await response.json();
        return data as LearningFeedPostResponse;
    } catch (error) {
        console.error('Error getting learning feed posts:', error);
        throw error;
    }
}

async function getLearningFeedPost(postId: string) : Promise<LearningFeedPost | null> {
    try {
        const response = await fetch(`/api/feed/post/${postId}`, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': getCurrentBearerToken()
            }
        });

        const data = await response.json();

        if (data.success) {
            return data.post as LearningFeedPost;
        } else {
            throw new Error(data.error ?? 'Unknown error');
        }
    } catch (error) {
        console.error('Error getting learning feed post:', error);
        throw error;
    }
}


async function reactToPost({postId, reactions, addReaction, removeReaction}: {postId: string, reactions: string[], addReaction?: string[], removeReaction?: string[]}) : Promise<LearningFeedReactResponse> {
    try {
        const response = await fetch(`/api/feed/post/${postId}/react`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': getCurrentBearerToken()
            },
            body: JSON.stringify({ reactions, addReaction, removeReaction } as ReactRequestBody)
        });

        const data = await response.json();
        return data as LearningFeedReactResponse;
    } catch (error) {
        console.error('Error reacting to post:', error);
        throw error;
    }
}

async function updateQuizResponses(postId: string, quizResponses: { [question: string]: number }) : Promise<UpdateQuizResponse> {
    try {
        const response = await fetch(`/api/feed/post/${postId}/quiz`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': getCurrentBearerToken()
            },
            body: JSON.stringify({ quizResponses } as UpdateQuizRequestBody)
        });

        const data = await response.json();
        return data as UpdateQuizResponse;
    } catch (error) {
        console.error('Error updating quiz responses:', error);
        throw error;
    }
}

async function sendFeedPostTelemetry(postId: string, telemetry: Partial<LearningFeedPostViewTelemetry>[]) : Promise<LearningFeedTelemetryResponse> {
    try {
        const response = await fetch(`/api/feed/learning/telemetry`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': getCurrentBearerToken()
            },
            body: JSON.stringify({ postId, telemetry })
        });

        const data = await response.json();
        return data as LearningFeedTelemetryResponse;
    } catch (error) {
        console.error('Error sending feed post telemetry:', error);
        throw error;
    }
}

async function getFeedUiTranslations(language: string) : Promise<{topics: Record<string, string>, learningFocus: Record<string, string>}> {
    try {
        const response = await fetch(`/api/feed/ui-translations/${language}`, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': getCurrentBearerToken()
            }
        });

        const data = await response.json();
        return (data as any).translations as {topics: Record<string, string>, learningFocus: Record<string, string>};
    } catch (error) {
        console.error('Error getting feed ui translations:', error);
        throw error;
    }
}

async function addContact(contactName: string, language: string) : Promise<{success: boolean, contacts?: UserContactInfo[], error?: string}> {
    try {
        const response = await fetch(`/api/contacts/add`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': getCurrentBearerToken()
            },
            body: JSON.stringify({ contactName, language })
        });

        const data = await response.json();
        return data as {success: boolean, contacts?: UserContactInfo[], error?: string};
    } catch(e) {
        console.error('Error adding contact:', e);
        throw e;
    }
}

async function sendGameDied(gameId: string, language: string, hits?: number) : Promise<{success: boolean, error?: string}> {
    try {
        const response = await fetch(`/api/games/${gameId}/died`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': getCurrentBearerToken()
            },
            body: JSON.stringify({language: language, hits: hits})            
        });

        const data = await response.json();
        return data as {success: boolean, error?: string};
    } catch (error) {
        console.error('Error game died:', error);
        throw error;
    }
}

async function sendGameOneUp(gameId: string, language: string, lives?: number) : Promise<{success: boolean, error?: string}> {
    try {
        const response = await fetch(`/api/games/${gameId}/oneup`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': getCurrentBearerToken()
            },
            body: JSON.stringify({language: language, lives: lives})            
        });

        const data = await response.json();
        return data as {success: boolean, error?: string};
    } catch (error) {
        console.error('Error game one up:', error);
        throw error;
    }
}

async function addUserVocab({word, language, nativeLanguage, definition, parentPhrase, fromExplainer}: {word: string, language: string, nativeLanguage: string, definition: string, parentPhrase?: string, fromExplainer?: boolean}) : Promise<{success: boolean, error?: string}> {
    try {
        const response = await fetch(`/api/vocab/add`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': getCurrentBearerToken()
            },
            body: JSON.stringify({ word, language, nativeLanguage, definition, parentPhrase, fromExplainer })
        });

        const data = await response.json();
        return data as {success: boolean, error?: string};
    } catch (error) {
        console.error('Error adding vocab:', error);
        throw error;
    }
}

async function userVocabExists({word, language}: {word: string, language: string}) : Promise<{success: boolean, exists: boolean, error?: string}> {
    try {
        const response = await fetch(`/api/vocab/exists`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': getCurrentBearerToken()
            },
            body: JSON.stringify({ word, language })
        });

        const data = await response.json();
        return data as {success: boolean, exists: boolean, error?: string};
    } catch (error) {
        console.error('Error checking if vocab exists:', error);
        throw error;
    }
}

async function getUserVocab(language: string, nativeLanguage: string, limit: number, skip: number) : Promise<{userVocab: Partial<UserVocab>[], totalCount: number}> {
    try {
        const response = await fetch(`/api/vocab?language`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': getCurrentBearerToken()
            },
            body: JSON.stringify({ language, nativeLanguage, limit, skip })
        });

        const data = await response.json();

        if (data.success) {
            return { userVocab: data.userVocab as Partial<UserVocab>[], totalCount: data.totalCount as number };
        } else {
            throw new Error(data.error ?? 'Unknown error');
        }
    } catch (error) {
        console.error('Error getting user vocab:', error);
        throw error;
    }
}
        
// Depreciated, use frontendApi instead
export const api = {
    createExamSession,
    loadExamSession,
    loadSessionsForExam,
    requestExamAssessment,
    completeExamSession,
    createPracticeSession,
    loadPracticeSessions,
    loadPracticeSession,
    completePracticeSession,
    loadUserLangProgress,
    updateUserPreferences,
    loadUserLangLevels,
    loadTutors,
    loadUserLangProgressVocab,
    loadUserLangProgressCorrections,
    markVocabLearned,
    markVocabDismissed,
    markCorrectionDismissed,
    sendPracticeSessionSummary,
    requestPracticeSessionAssessment,
    loadPracticeSessionCustomization,
    submitOnboardingForm,
    loadCurrentUser,
    getPracticeSessionResponseRecommendations,
    getExamSessionResponseRecommendations,
    updatePracticeSessionConversationId,
    updateExamSessionConversationId,
    notifyPracticeConversationStarted,
    notifyExamConversationStarted,
    getTranslationForPracticeSession,
    getTranslationForExamSession,
    getTranslationForChat,
    getContentBlock,
    getLastBillingTransaction,
    sendTelegramExplainer,
    claimCoupon,
    getSupportTickets,
    createSupportTicket,
    createUserLangProgressEvent,
    getVocabListenUrl,
    sendAudioMessage,
    testVoice,
    getExamSessionUrl,
    getTranslationsFromOtherLanguage,
    sendTgChatExercise,
    evCreate
};

// Alternative API-wrapper with semantic structure
export const frontendApi = {
    articles: {
        getLearningArticleById,
        getLearningArticlesForList,
    },
    user: {
        loadCurrentUser,
        updateUserPreferences,
        loadUserLangLevels,
        loadUserLangProgress,
        loadUserLangProgressVocab,
        loadUserLangProgressCorrections,
        markVocabLearned,
        markVocabDismissed,
        markCorrectionDismissed,
        createUserLangProgressEvent,
        submitOnboardingForm,
        deleteAccount,
        getUserRewards,
        initSession,
    },
    tutors: {
        loadTutors,
        loadTutor,
    },
    chat: {
        getChat,
        getChats,
        getChatsForList,
        createChat,
        getChatMessage,
        getChatMessages,
        sendChatMessage,
        getChatMessageAudio,
        processChatCorrections,
        getChatGoals,
        completeChat,
        completeChatTasks,
        getChatResponseRecommendations,
    },
    practice: {
        loadPracticeSessions,
        loadPracticeSession,
        completePracticeSession,
        sendPracticeSessionSummary,
        requestPracticeSessionAssessment,
        loadPracticeSessionCustomization,
    },
    exam: {
        loadExamSession,
        loadSessionsForExam,
        requestExamAssessment,
        completeExamSession,
        sendExamSummary
    },
    exercises: {
        sendTgChatExercise,
    },
    content: {
        getContentBlock,
    },
    billing: {
        getLastBillingTransaction,
        claimCoupon,
    },
    support: {
        getSupportTickets,
    },
    assistiveSpeech: {
        getAssistiveSpeechUrl,        
    },
    utils: {
        evCreate
    },
    explain: {
        getWordExplanationPage,
    },
    todoLists: {
        getTodoList,
        setTodoItem,
        getTodoItem,
    },
    feed: {
        getLearningFeedPost,
        getLearningFeedPosts,
        reactToPost,
        updateQuizResponses,
        sendFeedPostTelemetry,
        getFeedUiTranslations,
    },
    contacts: {
        addContact,
    },
    games: {
        sendGameDied,
        sendGameOneUp,
    },
    vocab: {
        addUserVocab,
        userVocabExists,
        getUserVocab,
    }
}
